import { useEffect, useRef, type RefObject } from "react";
import posthog from "posthog-js";

type CampaignEventProperties = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    _paq?: Array<[string, ...unknown[]]>;
  }
}

const MATOMO_CATEGORY = "LinkedIn Campaign";

const buildMatomoLabel = (properties: CampaignEventProperties) => {
  const location = properties.location ? String(properties.location) : "";
  const target = properties.target ? String(properties.target) : "";
  if (location && target) return `${location} -> ${target}`;
  return location || target || undefined;
};

export const captureCampaignEvent = (
  eventName: string,
  properties: CampaignEventProperties = {},
) => {
  try {
    posthog.capture(eventName, properties);
  } catch (error) {
    console.debug("PostHog campaign capture skipped", error);
  }

  try {
    if (typeof window === "undefined" || !Array.isArray(window._paq)) {
      return;
    }

    const label = buildMatomoLabel(properties);
    if (label) {
      window._paq.push(["trackEvent", MATOMO_CATEGORY, eventName, label]);
      return;
    }

    window._paq.push(["trackEvent", MATOMO_CATEGORY, eventName]);
  } catch (error) {
    console.debug("Matomo campaign capture skipped", error);
  }
};

export const useCampaignPageView = (
  eventName: string,
  properties: CampaignEventProperties = {},
) => {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    captureCampaignEvent(eventName, properties);
  }, [eventName, properties]);
};

export const useCampaignSectionView = (
  ref: RefObject<HTMLElement | null>,
  eventName: string,
  properties: CampaignEventProperties = {},
) => {
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || hasTrackedRef.current || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (!isVisible || hasTrackedRef.current) return;
        hasTrackedRef.current = true;
        captureCampaignEvent(eventName, properties);
        observer.disconnect();
      },
      {
        rootMargin: "0px 0px -20% 0px",
        threshold: 0.35,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [eventName, properties, ref]);
};
